import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { MediaAsset } from '@cinegrid/core';

/** App-styled modal dialog (replaces window.prompt / alert). */
export function AppDialog({
  open,
  title,
  children,
  onClose,
  onConfirm,
  confirmLabel = 'OK',
  cancelLabel = 'Cancel',
  confirmDisabled,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
  onConfirm: () => void;
  confirmLabel?: string;
  /** Pass null to hide the cancel button (info dialogs). */
  cancelLabel?: string | null;
  confirmDisabled?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/55 p-4"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-[340px] border border-hairline bg-panel shadow-[0_12px_40px_rgba(0,0,0,0.45)]"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="border-b border-hairline px-3 py-2.5">
          <h2 className="text-xs font-semibold tracking-wide text-text-high">{title}</h2>
        </div>
        <div className="px-3 py-3">{children}</div>
        <div className="flex items-center justify-end gap-2 border-t border-hairline px-3 py-2.5">
          {cancelLabel != null ? (
            <button
              type="button"
              className="border border-hairline bg-raised px-3 py-1.5 text-2xs text-text-high hover:border-[var(--cg-border-strong)]"
              onClick={onClose}
            >
              {cancelLabel}
            </button>
          ) : null}
          <button
            type="button"
            disabled={confirmDisabled}
            className="bg-accent px-3 py-1.5 text-2xs font-semibold text-base disabled:opacity-40"
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function RenameDialog({
  open,
  title = 'Rename',
  initialValue,
  onClose,
  onSubmit,
}: {
  open: boolean;
  title?: string;
  initialValue: string;
  onClose: () => void;
  onSubmit: (value: string) => void;
}) {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setValue(initialValue);
    const t = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open, initialValue]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
  };

  return (
    <AppDialog
      open={open}
      title={title}
      onClose={onClose}
      onConfirm={submit}
      confirmLabel="Rename"
      confirmDisabled={!value.trim()}
    >
      <label className="block">
        <span className="mb-1.5 block text-2xs uppercase tracking-wider text-text-low">Name</span>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          className="w-full border border-hairline bg-base px-2.5 py-2 text-xs text-text-high outline-none focus:border-accent"
        />
      </label>
    </AppDialog>
  );
}

export function AddVideoLayerDialog({
  open,
  initialName,
  onClose,
  onSubmit,
}: {
  open: boolean;
  initialName: string;
  onClose: () => void;
  onSubmit: (name: string) => void;
}) {
  const [name, setName] = useState(initialName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setName(initialName);
    const t = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open, initialName]);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
  };

  return (
    <AppDialog
      open={open}
      title="Add Video Layer"
      onClose={onClose}
      onConfirm={submit}
      confirmLabel="Add"
      confirmDisabled={!name.trim()}
    >
      <label className="block">
        <span className="mb-1.5 block text-2xs uppercase tracking-wider text-text-low">Name</span>
        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          className="w-full border border-hairline bg-base px-2.5 py-2 text-xs text-text-high outline-none focus:border-accent"
        />
      </label>
    </AppDialog>
  );
}

export function AddAudioLayerDialog({
  open,
  initialName,
  onClose,
  onSubmit,
}: {
  open: boolean;
  initialName: string;
  onClose: () => void;
  onSubmit: (name: string, channels: 'mono' | 'stereo') => void;
}) {
  const [name, setName] = useState(initialName);
  const [channels, setChannels] = useState<'mono' | 'stereo'>('stereo');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setName(initialName);
    setChannels('stereo');
    const t = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open, initialName]);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSubmit(trimmed, channels);
  };

  return (
    <AppDialog
      open={open}
      title="Add Audio Layer"
      onClose={onClose}
      onConfirm={submit}
      confirmLabel="Add"
      confirmDisabled={!name.trim()}
    >
      <label className="mb-3 block">
        <span className="mb-1.5 block text-2xs uppercase tracking-wider text-text-low">Name</span>
        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          className="w-full border border-hairline bg-base px-2.5 py-2 text-xs text-text-high outline-none focus:border-accent"
        />
      </label>
      <fieldset>
        <legend className="mb-1.5 block text-2xs uppercase tracking-wider text-text-low">
          Channel format
        </legend>
        <div className="flex flex-col gap-1.5" role="radiogroup" aria-label="Channel format">
          {(
            [
              {
                id: 'stereo' as const,
                label: 'Stereo',
                hint: 'Left + Right · 2 channels',
              },
              {
                id: 'mono' as const,
                label: 'Mono',
                hint: 'Single channel',
              },
            ] as const
          ).map((opt) => {
            const selected = channels === opt.id;
            return (
              <label
                key={opt.id}
                className={`flex cursor-default items-start gap-2.5 border px-2.5 py-2 ${
                  selected
                    ? 'border-accent bg-accent/10'
                    : 'border-hairline bg-base hover:border-[var(--cg-border-strong)]'
                }`}
              >
                <span
                  className={`mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border ${
                    selected ? 'border-accent' : 'border-[var(--cg-border-strong)]'
                  }`}
                  aria-hidden
                >
                  {selected ? (
                    <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                  ) : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={`block text-xs font-medium ${
                      selected ? 'text-text-high' : 'text-text-high'
                    }`}
                  >
                    {opt.label}
                  </span>
                  <span className="block text-2xs text-text-low">{opt.hint}</span>
                </span>
                <input
                  type="radio"
                  name="audio-layer-channels"
                  value={opt.id}
                  checked={selected}
                  onChange={() => setChannels(opt.id)}
                  className="sr-only"
                />
              </label>
            );
          })}
        </div>
      </fieldset>
    </AppDialog>
  );
}

const COMMON_FRAME_RATES = [
  23.976, 24, 25, 29.97, 30, 48, 50, 59.94, 60,
] as const;

function formatFpsLabel(fps: number): string {
  if (Math.abs(fps - 23.976) < 0.01) return '23.976';
  if (Math.abs(fps - 29.97) < 0.01) return '29.97';
  if (Math.abs(fps - 59.94) < 0.01) return '59.94';
  return Number.isInteger(fps) ? String(fps) : fps.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}

export function InterpretFrameRateDialog({
  open,
  assetName,
  currentFps,
  nativeFps,
  onClose,
  onSubmit,
}: {
  open: boolean;
  assetName: string;
  currentFps: number;
  nativeFps: number;
  onClose: () => void;
  onSubmit: (fps: number) => void;
}) {
  const [value, setValue] = useState(String(currentFps));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setValue(formatFpsLabel(currentFps));
    const t = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open, currentFps]);

  const parsed = Number.parseFloat(value);
  const valid = Number.isFinite(parsed) && parsed >= 1 && parsed <= 240;

  const submit = () => {
    if (!valid) return;
    onSubmit(parsed);
  };

  return (
    <AppDialog
      open={open}
      title="Interpret Footage"
      onClose={onClose}
      onConfirm={submit}
      confirmLabel="OK"
      confirmDisabled={!valid}
    >
      <p className="mb-3 truncate text-2xs text-text-low" title={assetName}>
        {assetName}
      </p>
      <p className="mb-3 text-2xs text-text-low">
        Assume this frame rate:{' '}
        <span className="text-text-high">
          {formatFpsLabel(nativeFps)} fps native · {formatFpsLabel(currentFps)} fps current
        </span>
      </p>
      <label className="mb-2 block">
        <span className="mb-1.5 block text-2xs uppercase tracking-wider text-text-low">
          Frame rate
        </span>
        <input
          ref={inputRef}
          type="number"
          min={1}
          max={240}
          step="any"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          className="w-full border border-hairline bg-base px-2.5 py-2 text-xs text-text-high outline-none focus:border-accent"
        />
      </label>
      <div className="flex flex-wrap gap-1">
        {COMMON_FRAME_RATES.map((fps) => (
          <button
            key={fps}
            type="button"
            className={`border px-1.5 py-0.5 text-2xs ${
              Math.abs(parsed - fps) < 0.01
                ? 'border-accent bg-accent/15 text-accent'
                : 'border-hairline text-text-low hover:border-accent hover:text-accent'
            }`}
            onClick={() => setValue(formatFpsLabel(fps))}
          >
            {formatFpsLabel(fps)}
          </button>
        ))}
      </div>
    </AppDialog>
  );
}

function formatBytes(n: number): string {
  if (!n || n <= 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDurationMeta(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const ss = s.toFixed(3).padStart(6, '0');
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}`;
  return `${m}:${ss}`;
}

export function MediaPropertiesDialog({
  open,
  asset,
  onClose,
}: {
  open: boolean;
  asset: MediaAsset | null;
  onClose: () => void;
}) {
  if (!asset) {
    return (
      <AppDialog
        open={open}
        title="Properties"
        onClose={onClose}
        onConfirm={onClose}
        confirmLabel="Close"
        cancelLabel={null}
      >
        <p className="text-2xs text-text-low">No media selected.</p>
      </AppDialog>
    );
  }

  const native = asset.nativeFps ?? asset.fps;
  const rows: { label: string; value: string }[] = [
    { label: 'Name', value: asset.name },
    { label: 'Type', value: asset.kind },
    { label: 'MIME', value: asset.mimeType || '—' },
    {
      label: 'Frame size',
      value:
        asset.width > 0 && asset.height > 0
          ? `${asset.width} × ${asset.height}`
          : '—',
    },
    {
      label: 'Pixel aspect',
      value: '1.0 (square)',
    },
    {
      label: 'Frame rate',
      value:
        asset.kind === 'audio'
          ? '—'
          : `${formatFpsLabel(asset.fps)} fps${
              native !== asset.fps ? ` (native ${formatFpsLabel(native)})` : ''
            }`,
    },
    { label: 'Duration', value: formatDurationMeta(asset.duration) },
    {
      label: 'Frames',
      value:
        asset.kind === 'audio' || !(asset.fps > 0)
          ? '—'
          : String(Math.round(asset.duration * asset.fps)),
    },
    { label: 'File size', value: formatBytes(asset.sizeBytes) },
    {
      label: 'Created',
      value: asset.createdAt
        ? new Date(asset.createdAt).toLocaleString()
        : '—',
    },
    { label: 'Asset ID', value: asset.id },
  ];

  if (asset.kind === 'solid' && asset.solidColor) {
    rows.splice(3, 0, { label: 'Color', value: asset.solidColor });
  }

  return (
    <AppDialog
      open={open}
      title="Properties"
      onClose={onClose}
      onConfirm={onClose}
      confirmLabel="Close"
      cancelLabel={null}
    >
      <div className="max-h-[50vh] overflow-auto">
        <table className="w-full border-collapse text-2xs">
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-b border-hairline/80 align-top">
                <th className="w-[38%] py-1.5 pr-2 text-left font-normal uppercase tracking-wider text-text-low">
                  {row.label}
                </th>
                <td className="break-all py-1.5 text-text-high">{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppDialog>
  );
}
